import { CardContent, Checkbox, FormControlLabel, Grid, Typography, Box } from '@mui/material';
import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { MonetizationOn } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import SimpleCard from '../../../../../components/SimpleCard';
import WizardButtons from '../WizardButtons';
import agreementDocument from '../../../../../assets/documents/NEW_fayVen_Vendor_Agreement.pdf';
import {
  setVendorRetailPayload,
  createVendorRetailBooking,
} from '../../../../../core/vendor-retail/action';

export default function Charges({ previousStep }) {
  const dispatch = useDispatch();
  const { vendorRetailPayload, recentBookings } = useSelector((store) => store.vendorRetail);
  const { profile } = useSelector((store) => store.retailProfiles);

  const [loading, setLoading] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [expectedBookingCount, setExpectedBookingCount] = useState(0);

  const totalPrice = vendorRetailPayload?.totalPrice || 0;

  useEffect(() => {
    if (vendorRetailPayload?.payment_terms_accepted) {
      setAcceptTerms(vendorRetailPayload.payment_terms_accepted);
    }
  }, [vendorRetailPayload?.payment_terms_accepted]);

  // Monitor when all bookings are successfully created
  useEffect(() => {
    // console.log('🟡 [Charges] Expected:', expectedBookingCount, 'Actual:', recentBookings.length);
    // console.log('🟡 [Charges] recentBookings:', recentBookings);
    if (expectedBookingCount > 0 && recentBookings.length >= expectedBookingCount) {
      // All bookings created successfully, stop loading
      // console.log('🟡 [Charges] All bookings created! Stopping loading.');
      setLoading(false);
    }
  }, [recentBookings, expectedBookingCount]);

  useEffect(() => {
    if (acceptTerms) setAlertMessage('');
  }, [acceptTerms]);

  const handleChange = (e) => {
    setAcceptTerms(e.target.checked);
  };

  const onSubmit = async () => {
    if (!acceptTerms) {
      setAlertMessage('Accept payment terms is required');
      return;
    }
    setAlertMessage('');
    setLoading(true);

    try {
      // Update payload with payment terms acceptance
      dispatch(
        setVendorRetailPayload({
          paymentTermsAccepted: acceptTerms,
        }),
      );

      // Create booking(s) for each time slot
      const {
        vendorId,
        retailScheduleId,
        venueId,
        bookingDate,
        timeSlots,
        contactName,
        contactPhone,
        contactEmail,
      } = vendorRetailPayload;

      if (!timeSlots || timeSlots.length === 0) {
        setAlertMessage('No time slots selected');
        setLoading(false);
        return;
      }

      // Set expected count so useEffect knows when all bookings are created
      console.log('🟡 [Charges] Creating', timeSlots.length, 'bookings');
      setExpectedBookingCount(timeSlots.length);

      // Store expected count in Redux so WaitApproval page knows when all are done
      dispatch(
        setVendorRetailPayload({
          expectedBookingCount: timeSlots.length,
        }),
      );

      // Create bookings for each time slot
      const isBatchBooking = timeSlots.length > 1;

      timeSlots.forEach((slot, index) => {
        const bookingData = {
          vendorId,
          retailScheduleId,
          venueId,
          bookingDate,
          startTime: slot.start,
          endTime: slot.end,
          durationHours: 1,
          contactName,
          contactPhone,
          contactEmail,
          paymentTermsAccepted: acceptTerms,
          // Batch booking fields
          isBatchBooking,
          batchIndex: index,
          batchTotal: timeSlots.length,
          // Only pass all time slots info on the last booking
          batchTimeSlots: index === timeSlots.length - 1 ? timeSlots : undefined,
        };

        console.log(
          `🟡 [Charges] Dispatching booking ${index + 1}/${timeSlots.length}:`,
          bookingData,
        );
        // Dispatch booking creation (saga will handle async)
        dispatch(createVendorRetailBooking(bookingData));
      });
    } catch (err) {
      console.error('Error creating booking:', err);
      setAlertMessage('Failed to create booking. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div>
      <Grid container justifyContent="center" mb={2}>
        <Grid item>
          <Typography fontWeight="bold">Charges:</Typography>
        </Grid>
        <Grid item>
          <Typography marginLeft={2}>A breakdown of the retail space charges and fees</Typography>
        </Grid>
      </Grid>

      <SimpleCard variant="outlined">
        <CardContent>
          <Grid container marginBottom={2}>
            <Grid item>
              <Typography fontWeight="bold">Price Breakdown:</Typography>
            </Grid>
            <Grid item>
              <Typography marginLeft={2}>What you are paying for</Typography>
            </Grid>
          </Grid>

          <Grid container justifyContent="center">
            <Grid item xs={12} md={8}>
              {/* Booking Summary */}
              <Box mb={3} p={2} bgcolor="#F5F5F5" borderRadius={1}>
                <Typography variant="body2" fontWeight="bold" mb={1}>
                  Booking Summary
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Date: {vendorRetailPayload?.bookingDate || ''}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Time Slots: {vendorRetailPayload?.timeSlots?.length || 0} hour(s)
                </Typography>
                <Typography variant="body2" color="text.secondary" mb={1}>
                  {vendorRetailPayload?.timeSlots?.map((slot) => slot.display).join(', ') || ''}
                </Typography>
              </Box>

              {/* Price Details */}
              <Grid container marginBottom={2} justifyContent="space-between">
                <Grid item>
                  <Typography fontWeight="bold">
                    Retail Space ({vendorRetailPayload?.timeSlots?.length || 0} hour
                    {(vendorRetailPayload?.timeSlots?.length || 0) > 1 ? 's' : ''}):
                  </Typography>
                </Grid>
                <Grid item>
                  <Typography>$ {totalPrice.toFixed(2)} USD</Typography>
                </Grid>
              </Grid>

              {/* Total */}
              <Box borderTop="2px solid #EE7B57" pt={2} mt={2}>
                <Grid container marginBottom={2} justifyContent="space-between">
                  <Grid item>
                    <Typography fontSize="large" fontWeight="bold">
                      Total amount due:
                    </Typography>
                  </Grid>
                  <Grid item>
                    <Typography fontSize="large" fontWeight="bold" color="#EE7B57">
                      $ {totalPrice.toFixed(2)} USD
                    </Typography>
                  </Grid>
                </Grid>
                <Box display="flex" justifyContent="center" mt={2}>
                  <MonetizationOn fontSize="large" sx={{ color: '#3c99ba' }} />
                </Box>
              </Box>

              {/* Payment Terms */}
              <Box mt={3} p={2} bgcolor="#FFF5F2" borderRadius={1}>
                <Typography variant="body2" color="text.secondary" paragraph>
                  <strong>Payment Terms:</strong>
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  • Payment will be processed upon approval by the venue
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  • Cancellations may be subject to fees per venue policy
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  • Full payment is required to confirm your booking
                </Typography>
              </Box>
            </Grid>
          </Grid>

          {/* Agreement Checkbox */}
          <Grid container justifyContent="center" mt={3}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={acceptTerms}
                  onChange={handleChange}
                  name="agree_payment"
                  sx={{
                    color: '#EE7B57',
                    '&.Mui-checked': {
                      color: '#EE7B57',
                    },
                  }}
                />
              }
              label="I agree to the payment terms listed by FayVen."
            />
          </Grid>

          <Grid container justifyContent="center">
            <a
              href={agreementDocument}
              target="_blank"
              rel="noreferrer"
              style={{ color: '#EE7B57', textDecoration: 'underline' }}>
              Please download and read the following payment terms.
            </a>
          </Grid>

          {alertMessage && (
            <Grid container justifyContent="center" mt={2}>
              <Typography color="error">{alertMessage}</Typography>
            </Grid>
          )}
        </CardContent>
      </SimpleCard>

      <WizardButtons
        nextStep={onSubmit}
        nextLabelButtonType="button"
        loading={loading}
        previousStep={previousStep}
      />
    </div>
  );
}

Charges.propTypes = {
  previousStep: PropTypes.func.isRequired,
};
